import React from 'react';
import { twMerge } from 'tailwind-merge';
import { useRowTabIndex } from './row-tab-stop';

/**
 * How a list row stands out. Both are grey.
 * - pageOpen: the row of the page that is open. A screen reader hears it as
 *   the current page.
 * - highlighted: a picker's Accept target, or a row whose menu is open.
 */
export type RowMarking = 'pageOpen' | 'highlighted';

// The row's marking when it can have both reasons; the open page wins.
export const rowMarking = ({
    pageOpen,
    highlighted,
}: {
    pageOpen?: boolean;
    highlighted?: boolean;
}): RowMarking | undefined => {
    if (pageOpen) {
        return 'pageOpen';
    }
    if (highlighted) {
        return 'highlighted';
    }
    return undefined;
};

interface Props {
    children: React.ReactNode;
    marking?: RowMarking;
    className?: string;
    dataTestId?: string;
    onClick?: (
        event?:
            | React.MouseEvent<HTMLLIElement | HTMLDivElement>
            | React.KeyboardEvent<HTMLLIElement | HTMLDivElement>,
    ) => void;
}

const BaseListItem = ({
    children,
    marking,
    onClick = null,
    className,
    dataTestId,
}: Props) => {
    // Is used for CTI test
    const muiListItem = 'MuiListItem-root';
    const rowTabIndex = useRowTabIndex();

    const handleClick = (
        event:
            | React.MouseEvent<HTMLLIElement | HTMLDivElement>
            | React.KeyboardEvent<HTMLLIElement | HTMLDivElement>,
    ) => {
        if (onClick && event.currentTarget.contains(event.target as Node)) {
            onClick(event);
        }
    };

    return (
        <li className="list-none">
            <div
                tabIndex={rowTabIndex ?? 0}
                onClick={(event) => handleClick(event)}
                onKeyDown={(event) => {
                    // Keys pressed in a button inside the row stay with that
                    // button, so Enter on a call button places the call.
                    if (
                        event.target === event.currentTarget &&
                        (event.key === 'Enter' || event.key === ' ')
                    ) {
                        event.preventDefault();
                        handleClick(event);
                    }
                }}
                role="menuitem"
                aria-current={marking === 'pageOpen' ? 'page' : undefined}
                data-test-id={dataTestId}
                className={twMerge(
                    'group focus-ring-inset flex w-full cursor-pointer items-center justify-center gap-x-3 rounded-sm p-2 outline-hidden hover:bg-gray-200',
                    marking ? 'bg-gray-200' : '',
                    muiListItem,
                    className,
                )}
            >
                {children}
            </div>
        </li>
    );
};

export default BaseListItem;
